/**
 * PartsQuoteForm.jsx - the Parts Quote: quote info, bill-to / ship-to,
 * shipping and terms, and the parts lines picked from the catalog (Part #
 * fills the manufacturer, description, unit, lead time and price; overtype
 * anything; a part with no catalog price prints TBD). With Quotes & Rates
 * Edit, the Manager sheet's price override, discount and freight amount.
 */
import { blankPart } from './quoteDraft';
import { Btn, Field, Grid, MUTED, NumInput, Section, SelectInput, TextInput, currencyOf, fmtMoney, inputStyle, td, th } from './quoteUi';

const MAX_PARTS = 50;

export default function PartsQuoteForm({ draft, setDraft, settings, catalog, calc, canOverride }) {
  const h = draft.header || {};
  const cur = currencyOf(settings);
  const money = (v) => fmtMoney(v, cur);
  const lists = settings.lists || {};
  const lines = draft.lines || [];
  const setH = (k) => (v) => setDraft((d) => ({ ...d, header: { ...d.header, [k]: v } }));
  const setLine = (i, patch) => setDraft((d) => ({ ...d, lines: d.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) }));
  const byPart = new Map((catalog || []).map((c) => [String(c.partNo).toLowerCase(), c]));
  // picking a catalog part fills the line from the catalog (and clears what the last part filled)
  const pickPart = (i, partNo) => {
    const c = byPart.get(String(partNo).trim().toLowerCase());
    setLine(i, c ? { partNo, manufacturer: c.manufacturer, description: c.description, unit: c.unit, leadTime: c.leadTime } : { partNo });
  };
  // the calc's lines skip blank Part #s, so line i is the k-th one that has one
  const calcAt = [];
  let k = 0;
  lines.forEach((l, i) => {
    if (String(l.partNo || '').trim()) {
      calcAt[i] = ((calc && calc.lines) || [])[k];
      k += 1;
    }
  });

  return (
    <div>
      <Section title="A  Quote info" hint="Prefilled from the opportunity and its account. Anything can be overtyped.">
        <Grid cols={4}>
          <Field label="Offer No.">
            <TextInput value={h.offerNo} onChange={setH('offerNo')} />
          </Field>
          <Field label="Offer date">
            <TextInput type="date" value={h.offerDate} onChange={setH('offerDate')} />
          </Field>
          <Field label="Valid (days)">
            <NumInput value={h.validity} onChange={setH('validity')} />
          </Field>
          <Field label="Customer PO / RFQ">
            <TextInput value={h.rfqNo} onChange={setH('rfqNo')} placeholder="Per customer request" />
          </Field>
          <Field label="Quote title (subject)" span={2}>
            <TextInput value={h.subject} onChange={setH('subject')} />
          </Field>
          <Field label="Customer" span={2}>
            <TextInput value={h.customer} onChange={setH('customer')} />
          </Field>
          <Field label="Bill-to address 1">
            <TextInput value={h.addr1} onChange={setH('addr1')} />
          </Field>
          <Field label="Bill-to city, ST ZIP">
            <TextInput value={h.addr2} onChange={setH('addr2')} />
          </Field>
          <Field label="Attention (contact)">
            <TextInput value={h.attention} onChange={setH('attention')} placeholder="Purchasing" />
          </Field>
          <Field label="Payment terms">
            <TextInput value={h.payTerms} onChange={setH('payTerms')} />
          </Field>
          <Field label="Prepared by">
            <TextInput value={h.preparer} onChange={setH('preparer')} />
          </Field>
          <Field label="Preparer phone">
            <TextInput value={h.prepPhone} onChange={setH('prepPhone')} />
          </Field>
          <Field label="Preparer email" span={2}>
            <TextInput value={h.prepEmail} onChange={setH('prepEmail')} />
          </Field>
        </Grid>
      </Section>

      <Section title="Shipping and terms" hint="Ship-to left blank = the bill-to.">
        <Grid cols={4}>
          <Field label="Ship-to name">
            <TextInput value={h.shipName} onChange={setH('shipName')} placeholder={h.customer} />
          </Field>
          <Field label="Ship-to address 1">
            <TextInput value={h.shipAddr1} onChange={setH('shipAddr1')} placeholder={h.addr1} />
          </Field>
          <Field label="Ship-to city, ST ZIP">
            <TextInput value={h.shipAddr2} onChange={setH('shipAddr2')} placeholder={h.addr2} />
          </Field>
          <Field label="Ship via">
            <SelectInput value={h.shipVia} options={lists.shipVia || ['Courier']} onChange={setH('shipVia')} />
          </Field>
          <Field label="Freight terms" span={2}>
            <SelectInput value={h.freightTerms} options={lists.freightTerms || []} onChange={setH('freightTerms')} />
          </Field>
          <Field label="Lead time note" span={2}>
            <TextInput value={h.leadNote} onChange={setH('leadNote')} placeholder="Per line, ARO." />
          </Field>
          <Field label="Warranty" span={4}>
            <TextInput value={h.warranty} onChange={setH('warranty')} />
          </Field>
        </Grid>
      </Section>

      <Section
        title="B  Parts"
        hint="Pick a Part # from the catalog (or type a new one). A part with no price prints TBD and is left out of the total."
      >
        <datalist id="quote-part-list">
          {(catalog || []).map((c) => (
            <option key={c.id || c.partNo} value={c.partNo}>{`${c.manufacturer} - ${c.description}`}</option>
          ))}
        </datalist>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {[
                '#',
                'Part #',
                'Manufacturer',
                'Description',
                'Unit',
                'Lead time',
                'Qty',
                ...(canOverride ? ['Price override'] : []),
                'Unit price',
                'Extended',
                '',
              ].map((x) => (
                <th key={x} style={th}>
                  {x}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => {
              const c = calcAt[i] || null;
              return (
                <tr key={i}>
                  <td style={{ ...td, width: 22, fontSize: 11, color: MUTED }}>{i + 1}</td>
                  <td style={{ ...td, width: 140 }}>
                    <TextInput
                      aria-label={`Part ${i + 1} number`}
                      list="quote-part-list"
                      value={l.partNo}
                      onChange={(v) => pickPart(i, v)}
                    />
                  </td>
                  <td style={{ ...td, width: 120 }}>
                    <TextInput value={l.manufacturer} onChange={(v) => setLine(i, { manufacturer: v })} />
                  </td>
                  <td style={td}>
                    <TextInput
                      aria-label={`Part ${i + 1} description`}
                      value={l.description}
                      onChange={(v) => setLine(i, { description: v })}
                    />
                  </td>
                  <td style={{ ...td, width: 50 }}>
                    <TextInput value={l.unit} placeholder="ea" onChange={(v) => setLine(i, { unit: v })} />
                  </td>
                  <td style={{ ...td, width: 120 }}>
                    <TextInput value={l.leadTime} onChange={(v) => setLine(i, { leadTime: v })} />
                  </td>
                  <td style={{ ...td, width: 56 }}>
                    <NumInput aria-label={`Part ${i + 1} quantity`} value={l.qty} onChange={(v) => setLine(i, { qty: v })} />
                  </td>
                  {canOverride && (
                    <td style={{ ...td, width: 96 }}>
                      <NumInput
                        aria-label={`Part ${i + 1} price override`}
                        value={l.price}
                        placeholder="catalog"
                        onChange={(v) => setLine(i, { price: v })}
                      />
                    </td>
                  )}
                  <td style={{ ...td, width: 92, textAlign: 'right', fontSize: 11.5 }}>{c ? (c.tbd ? 'TBD' : money(c.price)) : ''}</td>
                  <td style={{ ...td, width: 100, textAlign: 'right', fontSize: 11.5, fontWeight: 600 }}>
                    {c ? (c.tbd ? 'TBD' : money(c.extended)) : ''}
                  </td>
                  <td style={{ ...td, width: 30 }}>
                    <Btn
                      small
                      danger
                      aria-label={`Remove part ${i + 1}`}
                      onClick={() => setDraft((d) => ({ ...d, lines: d.lines.filter((_, j) => j !== i) }))}
                    >
                      x
                    </Btn>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{ marginTop: 6 }}>
          <Btn
            small
            disabled={lines.length >= MAX_PARTS}
            onClick={() => setDraft((d) => ({ ...d, lines: [...(d.lines || []), blankPart()] }))}
          >
            + Add part
          </Btn>
        </div>
      </Section>

      {canOverride && (
        <Section
          title="Manager overrides"
          hint="Quotes & Rates Edit only (the workbook's Manager sheet). Freight blank = TBD on the quote."
        >
          <Grid cols={4}>
            <Field label="Discount (%)">
              <NumInput aria-label="Discount percent" value={h.discountPct} onChange={setH('discountPct')} />
            </Field>
            <Field label={`Freight / handling (${cur.symbol})`}>
              <NumInput aria-label="Freight amount" value={h.freightAmount} placeholder="TBD" onChange={setH('freightAmount')} />
            </Field>
          </Grid>
        </Section>
      )}

      <Section
        title="C  Notes"
        hint="Printed under the parts table: lead times, alternates, anything the customer should know. Each line is its own paragraph."
      >
        <textarea
          aria-label="Notes"
          value={draft.notes || ''}
          rows={4}
          onChange={(e) => {
            const v = e.target.value;
            setDraft((d) => ({ ...d, notes: v }));
          }}
          style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.45 }}
        />
      </Section>
    </div>
  );
}
